import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsers,
    faGift,
    faStore,
    faUserFriends,
    faTicketAlt,
    faPoll,
    faGamepad,
    faBoxes,
    faPercent,
    faUserShield,
    faRocket,
    faCheckCircle,
    faArrowRight,
    faStar,
    faChartLine,
    faShieldAlt,
    faHeadset
} from '@fortawesome/free-solid-svg-icons';

const modules = [
    {
        icon: faUsers,
        title: "Gestión de Marca",
        description: "Permite a los usuarios gestionar marcas, asignar tiendas y clientes, y establecer condiciones para la acumulación de puntos. Funcionalidades clave: creación, actualización y gestión de premios.",
        features: ["Gestión centralizada de marcas", "Asignación de tiendas", "Configuración de puntos", "Gestión de premios"],
        color: "from-blue-500 to-blue-600",
        seoKeywords: "gestión de marcas, sistema de puntos, premios por puntos, fidelización de clientes",
    },
    {
        icon: faGift,
        title: "Premios",
        description: "Administra los premios disponibles para canjear puntos, definiendo requerimientos y controlando su disponibilidad.",
        features: ["Catálogo de premios", "Configuración de puntos", "Control de inventario", "Canje automático"],
        color: "from-purple-500 to-purple-600",
        seoKeywords: "premios, canje de puntos, fidelización de clientes",
    },
    {
        icon: faStore,
        title: "Gestión de Tiendas",
        description: "Permite registrar y gestionar tiendas, administrar ubicación, contacto y redes sociales, vinculándolas a una marca.",
        features: ["Registro de tiendas", "Gestión de ubicaciones", "Información de contacto", "Integración con redes sociales"],
        color: "from-green-500 to-green-600",
        seoKeywords: "gestión de tiendas, administración de ubicaciones, software para tiendas",
    },
    {
        icon: faUserFriends,
        title: "Gestión de Clientes",
        description: "Facilita la administración integral de la información de clientes, segmentación dinámica y seguimiento de interacciones.",
        features: ["CRM integrado", "Segmentación automática", "Historial de compras", "Análisis de comportamiento"],
        color: "from-orange-500 to-orange-600",
        seoKeywords: "gestión de clientes, CRM, fidelización de clientes",
    },
    {
        icon: faTicketAlt,
        title: "Gestión de Tickets y Puntos",
        description: "Registra compras como tickets, acumula puntos y permite el canje de premios, ofreciendo análisis detallado de transacciones.",
        features: ["Registro de compras", "Acumulación automática", "Canje de premios", "Análisis de transacciones"],
        color: "from-red-500 to-red-600",
        seoKeywords: "sistema de puntos, gestión de tickets, programa de recompensas",
    },
    {
        icon: faPoll,
        title: "Encuestas de Satisfacción",
        description: "Crea y distribuye encuestas para recoger feedback, analizar resultados en tiempo real y obtener insights valiosos.",
        features: ["Creación de encuestas", "Distribución automática", "Análisis en tiempo real", "Reportes detallados"],
        color: "from-indigo-500 to-indigo-600",
        seoKeywords: "encuestas de satisfacción, feedback del cliente, análisis de cliente",
    },
    {
        icon: faGamepad,
        title: "Fidelización Basada en Juego (GameLoyalty)",
        description: "Incorpora elementos de gamificación para mejorar la interacción y fidelización mediante campañas lúdicas con premios.",
        features: ["Campañas gamificadas", "Sistema de niveles", "Recompensas interactivas", "Competencias entre clientes"],
        color: "from-pink-500 to-pink-600",
        seoKeywords: "gamificación de clientes, fidelización por juego, campañas de gamificación",
    },
    {
        icon: faBoxes,
        title: "Gestión de Productos",
        description: "Administra el inventario de productos, control de stock y asociación de productos a tiendas para una gestión eficiente.",
        features: ["Control de inventario", "Gestión de stock", "Asociación a tiendas", "Alertas de stock bajo"],
        color: "from-teal-500 to-teal-600",
        seoKeywords: "gestión de inventario, control de stock, administración de productos",
    },
    {
        icon: faPercent,
        title: "Regalos y Descuentos",
        description: "Gestiona recompensas y descuentos, genera códigos de recompensa y facilita promociones integradas con el sistema de tickets.",
        features: ["Gestión de descuentos", "Códigos promocionales", "Promociones automáticas", "Integración con tickets"],
        color: "from-yellow-500 to-yellow-600",
        seoKeywords: "gestión de recompensas, sistema de descuentos, promociones personalizadas",
    },
    {
        icon: faUserShield,
        title: "Gestión de Trabajadores",
        description: "Permite el registro, autenticación y administración de trabajadores, definiendo roles y accesos para proteger la plataforma.",
        features: ["Registro de trabajadores", "Control de acceso", "Gestión de roles", "Autenticación segura"],
        color: "from-gray-500 to-gray-600",
        seoKeywords: "gestión de trabajadores, autenticación segura, control de acceso",
    },
];

const FeaturesPage = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [activeModule, setActiveModule] = useState(0);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    const benefits = [
        {
            icon: faRocket,
            title: "Implementación Rápida",
            description: "Configura tu programa de fidelización en menos de 30 minutos"
        },
        {
            icon: faChartLine,
            title: "Resultados Inmediatos",
            description: "Ve el impacto en tus ventas desde el primer mes"
        },
        {
            icon: faShieldAlt,
            title: "Seguridad Garantizada",
            description: "Protección de datos y cumplimiento GDPR"
        },
        {
            icon: faHeadset,
            title: "Soporte 24/7",
            description: "Asistencia técnica disponible cuando la necesites"
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <Helmet>
                <title>
                    Características de Software de Fidelización – Funcionalidades de Programa de Lealtad Digital
                </title>
                <meta
                    name="description"
                    content="Descubre las funcionalidades que diferencian a nuestro ecosistema de fidelización digital. Gestión de marca, premios, tiendas, clientes y más, diseñados para maximizar la lealtad y las ventas."
                />
                <meta
                    name="keywords"
                    content="características de software de fidelización, funcionalidades de programa de lealtad digital, gestión de marcas, premios, fidelización de clientes"
                />
            </Helmet>

            {/* Hero Section */}
            <header className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-20">
                <div className="container mx-auto px-4 text-center">
                    <h1 className="text-5xl lg:text-6xl font-bold mb-6">
                        Funcionalidades que
                        <span className="text-yellow-400 block">Transforman Negocios</span>
                    </h1>
                    <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
                        PetitPrime combina las mejores herramientas de fidelización en una plataforma
                        intuitiva diseñada específicamente para pymes y comercios.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/register"
                            className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold py-4 px-8 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center"
                        >
                            <FontAwesomeIcon icon={faRocket} className="mr-2" />
                            Prueba Gratis 30 Días
                        </Link>
                        <Link
                            to="/pricing"
                            className="border-2 border-white hover:bg-white hover:text-blue-600 text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 flex items-center justify-center"
                        >
                            Ver Planes y Precios
                        </Link>
                    </div>
                </div>
            </header>

            {/* Benefits Section */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-800 mb-4">
                            ¿Por qué Elegir PetitPrime?
                        </h2>
                        <p className="text-xl text-gray-600">
                            Descubre las ventajas que nos hacen únicos en el mercado
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {benefits.map((benefit, index) => (
                            <div
                                key={index}
                                className="text-center p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 hover:shadow-lg transition-all duration-300"
                            >
                                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <FontAwesomeIcon icon={benefit.icon} className="text-2xl text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">{benefit.title}</h3>
                                <p className="text-gray-600">{benefit.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mb-4">
                            Funcionalidades Completas
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Todo lo que necesitas para crear y gestionar programas de fidelización exitosos
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {modules.map((module, index) => (
                            <div
                                key={index}
                                className={`group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 cursor-pointer ${activeModule === index ? 'ring-2 ring-blue-500' : ''
                                    }`}
                                onClick={() => setActiveModule(index)}
                            >
                                <div className={`w-16 h-16 bg-gradient-to-r ${module.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    <FontAwesomeIcon icon={module.icon} className="text-2xl text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-4">{module.title}</h3>
                                <p className="text-gray-600 mb-6 leading-relaxed">{module.description}</p>

                                {/* Features List */}
                                <div className="space-y-2">
                                    {module.features.map((feature, featureIndex) => (
                                        <div key={featureIndex} className="flex items-center text-sm text-gray-600">
                                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 flex-shrink-0" />
                                            <span>{feature}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* SEO Keywords */}
                                <div className="mt-6 pt-4 border-t border-gray-100">
                                    <p className="text-xs text-gray-500">
                                        <strong>Keywords:</strong> {module.seoKeywords}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-4xl font-bold mb-6">
                        ¿Listo para Implementar tu Programa de Fidelización?
                    </h2>
                    <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
                        Únete a cientos de empresas que ya están transformando su relación con los clientes
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/register"
                            className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold py-4 px-8 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center"
                        >
                            <FontAwesomeIcon icon={faRocket} className="mr-2" />
                            Comenzar Prueba Gratis
                            <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                        </Link>
                        <Link
                            to="/demo"
                            className="border-2 border-white hover:bg-white hover:text-blue-600 text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 flex items-center justify-center"
                        >
                            Solicitar Demo Personalizada
                        </Link>
                    </div>
                    <div className="mt-8 text-blue-200 text-sm">
                        <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-green-400" />
                        Sin compromiso • Configuración en 30 minutos • Soporte incluido
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FeaturesPage;
